import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, AlertTriangle, CheckCircle, Clock,
  ChevronRight, BookOpen, Download, X,
  Printer, Save, Heart, Info, FileDown,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

// Helper function to normalize special characters for PDF
const normalizeForPDF = (text) => {
  if (!text) return '';
  
  let result = String(text);
  
  // Spanish characters to ASCII
  result = result
    .replace(/á/g, 'a').replace(/Á/g, 'A')
    .replace(/é/g, 'e').replace(/É/g, 'E')
    .replace(/í/g, 'i').replace(/Í/g, 'I')
    .replace(/ó/g, 'o').replace(/Ó/g, 'O')
    .replace(/ú/g, 'u').replace(/Ú/g, 'U')
    .replace(/ñ/g, 'n').replace(/Ñ/g, 'N')
    .replace(/ü/g, 'u').replace(/Ü/g, 'U')
    .replace(/¿/g, '').replace(/¡/g, '')
    .replace(/–/g, '-').replace(/—/g, '-')
    .replace(/"/g, '"').replace(/"/g, '"')
    .replace(/'/g, "'").replace(/'/g, "'")
    .replace(/…/g, '...')
    .replace(/°/g, 'o')
    .replace(/€/g, 'EUR')
    .replace(/•/g, '-');
  
  // Remove emojis and non-ASCII characters
  let cleaned = '';
  for (let i = 0; i < result.length; i++) {
    const code = result.charCodeAt(i);
    if (code <= 127) {
      cleaned += result[i];
    }
  }
  
  return cleaned;
};

// ============================================
// TIPOS DE EMERGENCIA - EXPANDIDO CON 4 NUEVOS
// ============================================
const EMERGENCY_TYPES = [
  { id: "dana", label: "DANA", icon: "", color: "#3b82f6", description: "Depresión Aislada en Niveles Altos - lluvias torrenciales" },
  { id: "inundacion", label: "Inundación", icon: "", color: "#06b6d4", description: "Desbordamiento de ríos o acumulación de agua" },
  { id: "incendio", label: "Incendio", icon: "", color: "#ef4444", description: "Fuego forestal o estructural" },
  { id: "terremoto", label: "Terremoto", icon: "", color: "#78716c", description: "Movimiento sísmico y réplicas" },
  { id: "tsunami", label: "Tsunami", icon: "", color: "#1e40af", description: "Ola gigante tras terremoto submarino" },
  { id: "volcanico", label: "Erupción Volcánica", icon: "", color: "#dc2626", description: "Actividad volcánica, ceniza y lava" },
  { id: "huracan", label: "Huracán/Ciclón", icon: "", color: "#8b5cf6", description: "Vientos extremos y lluvias" },
  { id: "tornado", label: "Tornado", icon: "", color: "#6366f1", description: "Viento giratorio violento" },
  { id: "nevada", label: "Nevada/Ola de Frío", icon: "", color: "#0ea5e9", description: "Nevadas intensas y temperaturas extremas" },
  { id: "ola_calor", label: "Ola de Calor", icon: "", color: "#f97316", description: "Temperaturas extremadamente altas" },
  { id: "sequia", label: "Sequía", icon: "", color: "#f59e0b", description: "Escasez prolongada de agua" },
  { id: "tecnologico", label: "Riesgo Tecnológico", icon: "", color: "#a855f7", description: "Accidente químico, nuclear o industrial" },
  { id: "epidemia", label: "Epidemia Animal", icon: "", color: "#16a34a", description: "Brote de enfermedad en animales" },
];

// ============================================
// TIPS DE APOYO EMOCIONAL - BASADOS EN GUÍA 9
// ============================================
const EMOTIONAL_TIPS = {
  before: {
    title: "Preparación emocional",
    tips: [
      "Habla con tu familia sobre los riesgos de forma calmada, sin generar alarma",
      "Los animales perciben tu estado emocional - mantén la calma durante la preparación",
      "Practica los planes con tus animales para que se acostumbren al transportín y rutas",
      "Crea una 'caja de calma' con objetos reconfortantes para ti y tus animales",
    ]
  },
  during: {
    title: "Mantén la calma",
    tips: [
      "Respira profundo: inhala 3 segundos, exhala 6-8 segundos",
      "Tu serenidad es contagiosa - los animales se calman si tú estás tranquilo/a",
      "Habla a tus animales con voz suave y tranquilizadora",
      "Es normal sentir miedo - reconócelo pero no dejes que te paralice",
    ]
  },
  after: {
    title: "Recuperación emocional",
    tips: [
      "Es normal sentirse raro, triste o ansioso después de una emergencia",
      "Los animales también pueden mostrar estrés post-traumático: paciencia y cariño",
      "Vuelve a las rutinas lo antes posible - da seguridad a humanos y animales",
      "Si los síntomas persisten más de 2 semanas, busca ayuda profesional",
    ]
  }
};

// ============================================
// GUÍAS DE EMERGENCIA - MASIVAMENTE ENRIQUECIDAS
// Fuentes: Ready.gov, AVMA, Cruz Roja, FEMA, 
// Protección Civil España, AEMET, documentos LATAM
// ============================================
const EMERGENCY_GUIDES = {
  dana: {
    before: [
      { text: "Revisa las alertas de AEMET y activa las notificaciones de ES-Alert en tu móvil", essential: true },
      { text: "Prepara la mochila de vida con comida (3 días mínimo), agua y medicamentos para tus animales", essential: true },
      { text: "Identifica rutas de evacuación y refugios que admitan animales - llama antes para confirmar", essential: true },
      { text: "Asegúrate de que el microchip de tus animales está actualizado con tu teléfono actual", essential: true },
      { text: "Ten el transportín siempre accesible y con una manta familiar dentro (olor reconfortante)", essential: true },
      { text: "Localiza las zonas altas de tu casa y marca mentalmente dónde refugiarte con animales", essential: true },
      { text: "Guarda documentación de animales (cartilla, vacunas, fotos) en bolsa impermeable", essential: true },
      { text: "Infórmate sobre el nivel de riesgo de tu zona en el Plan INUNCAT o similar de tu comunidad", essential: false },
      { text: "Carga dispositivos móviles y ten baterías externas listas", essential: false },
      { text: "Revisa bajantes y desagües - despéjalos de hojas y residuos", essential: false },
      { text: "Si tienes animales de granja, identifica terreno elevado y prepara acceso", essential: false },
    ],
    during: [
      { text: "NO salgas a la calle si hay alerta roja - ni por los animales que estén fuera", essential: true, warning: true },
      { text: "Mantén a tus animales dentro de casa, en una zona segura y ALTA", essential: true },
      { text: "Si hay riesgo de inundación, sube a plantas superiores CON tus animales ANTES de que suba el agua", essential: true },
      { text: "NO intentes cruzar zonas inundadas - 15cm de agua pueden derribarte, 30cm arrastrar un coche", essential: true, warning: true },
      { text: "Mantén la calma - los animales perciben tu estrés y pueden entrar en pánico", essential: true },
      { text: "Si debes evacuar, hazlo por rutas seguras indicadas por autoridades, NUNCA por atajos", essential: true },
      { text: "Lleva SIEMPRE a tus animales contigo - no vuelvas a por ellos después", essential: true, warning: true },
      { text: "Evita sótanos, garajes, pasos subterráneos y zonas bajas", essential: true, warning: true },
      { text: "Si quedas atrapado, llama al 112, sube al punto más alto e indica que tienes animales", essential: true },
      { text: "Desconecta electricidad y gas si el agua comienza a entrar", essential: true },
      { text: "Si tienes caballos/ganado, NO los dejes atados - suéltalos en zona alta si no puedes evacuarlos", essential: false },
    ],
    after: [
      { text: "No regreses a casa hasta que las autoridades lo autoricen oficialmente", essential: true },
      { text: "Revisa el estado de tus animales: estrés, heridas, deshidratación, hipotermia", essential: true },
      { text: "Inspecciona la vivienda ANTES de entrar con animales (daños estructurales, cables, gas)", essential: true },
      { text: "Ofrece agua fresca y limpia - los animales pueden estar muy deshidratados", essential: true },
      { text: "NO dejes que beban agua estancada o de charcos - puede estar contaminada", essential: true, warning: true },
      { text: "Mantén a los animales alejados de escombros y cables eléctricos caídos", essential: true },
      { text: "Si perdiste a tu animal, regístralo en Emergencia Animal Conecta y difunde en redes", essential: true },
      { text: "Contacta con tu veterinario si observas: tos, diarrea, vómitos, apatía extrema", essential: true },
      { text: "Desinfecta TODO lo que tocó agua sucia: camas, comederos, juguetes", essential: true },
      { text: "Los animales pueden mostrar estrés días después: ofrece rutina y tranquilidad", essential: false },
      { text: "Revisa que no hayan ingerido sustancias tóxicas arrastradas por el agua", essential: false },
    ],
    animalSpecific: {
      perros: ["Mantén la correa puesta incluso en casa - pueden asustarse y huir", "Los perros grandes pueden nadar pero se agotan rápido"],
      gatos: ["Los gatos se esconden cuando tienen miedo - revisa armarios, bajo camas, electrodomésticos", "Usa feromona Feliway para calmarlos si tienes"],
      aves: ["Cubre parcialmente la jaula con tela oscura para reducir estrés", "El estrés puede causar que dejen de comer - vigila"],
      reptiles: ["Necesitan calor - ten preparada una fuente de calor portátil", "Sin calor adecuado pueden entrar en letargo peligroso"],
      caballos: ["Si no puedes evacuar, márcalos con tu teléfono (rotulador resistente al agua)", "NUNCA los dejes atados en zona inundable"],
      ganado: ["Mueve el ganado a zonas altas ANTES de la emergencia", "Los animales en grupo se estresan menos"],
    }
  },
  
  inundacion: {
    before: [
      { text: "Infórmate sobre si tu zona es propensa a inundaciones (consulta mapas de riesgo de tu ayuntamiento)", essential: true },
      { text: "Prepara un kit de emergencia con suministros para 72 horas para humanos y animales", essential: true },
      { text: "Identifica el refugio más cercano que acepte animales - llama para confirmar política", essential: true },
      { text: "Ten el transportín siempre accesible, limpio y con manta con olor familiar", essential: true },
      { text: "Guarda los documentos de tus animales (cartilla, vacunas, fotos) en bolsa impermeable", essential: true },
      { text: "Practica evacuaciones con tus animales - que se acostumbren al transportín y al coche", essential: true },
      { text: "Ten preparada una mochila de evacuación específica para cada animal", essential: true },
      { text: "Instala barreras o sacos de arena si vives en zona de riesgo recurrente", essential: false },
      { text: "Identifica vecinos con animales para ayuda mutua en evacuación", essential: false },
      { text: "Si tienes acuarios grandes, ten preparado un sistema de oxigenación portátil", essential: false },
    ],
    during: [
      { text: "Muévete a zonas altas INMEDIATAMENTE con tus animales - no esperes", essential: true },
      { text: "NO camines por agua en movimiento - 15cm pueden derribarte, 60cm arrastrar un coche", essential: true, warning: true },
      { text: "Desconecta electricidad y gas si el agua comienza a entrar", essential: true },
      { text: "Si estás en coche y el agua sube, SAL del vehículo con tus animales inmediatamente", essential: true, warning: true },
      { text: "Mantén a los animales en transportín o con correa corta para controlarlos", essential: true },
      { text: "Si el agua sube rápido, rompe el techo si es necesario para salir - lleva herramienta", essential: true },
      { text: "NO vuelvas a por posesiones - tu vida y la de tus animales es lo primero", essential: true, warning: true },
      { text: "Si tienes ganado/caballos, suéltalos en zona alta si no puedes evacuarlos", essential: false },
    ],
    after: [
      { text: "Espera confirmación OFICIAL antes de regresar - puede haber peligros ocultos", essential: true },
      { text: "Revisa daños estructurales ANTES de entrar con animales", essential: true },
      { text: "NO dejes que tus animales beban agua de charcos - bacterias, químicos, parásitos", essential: true, warning: true },
      { text: "Limpia y desinfecta TODO lo que tocó agua: comederos, camas, juguetes", essential: true },
      { text: "Observa a tus animales los días siguientes: diarrea, vómitos, tos, apatía", essential: true },
      { text: "El lodo puede contener químicos, aceites y bacterias - limpia bien las patas", essential: true },
      { text: "Revisa que la comida almacenada no se haya contaminado - en duda, deséchala", essential: true },
      { text: "Los animales estresados pueden mostrar cambios de comportamiento - paciencia", essential: false },
    ],
    animalSpecific: {
      perros: ["Pueden intentar beber agua sucia - vigila constantemente", "El barro en las patas puede causar dermatitis - limpia y seca bien"],
      gatos: ["Buscarán esconderse en altura - asegúrate de que puedan acceder a lugares altos seguros", "El estrés puede causar problemas urinarios - vigila"],
      pequeños_mamiferos: ["Conejos, cobayas, hámsters: evacúa con transportín pequeño, son muy sensibles al estrés", "Necesitan heno/comida específica - no improvises alimentación"],
    }
  },

  incendio: {
    before: [
      { text: "Si vives en zona forestal, crea un perímetro de 10-30m sin vegetación seca alrededor de tu casa", essential: true },
      { text: "Ten SIEMPRE lista la mochila de evacuación con 3 días de suministros para animales", essential: true },
      { text: "Infórmate sobre TODAS las rutas de evacuación y puntos de encuentro - practica con animales", essential: true },
      { text: "Todos los animales DEBEN tener microchip actualizado y collar con tu teléfono", essential: true },
      { text: "Ten preparados transportines para TODOS tus animales - uno por animal", essential: true },
      { text: "Haz una lista de refugios, hoteles y residencias que acepten animales", essential: true },
      { text: "Mantén el depósito del coche al menos medio lleno en temporada de incendios", essential: true },
      { text: "Ten una manguera larga conectada y lista para humedecer el perímetro si hay tiempo", essential: false },
      { text: "Guarda fotos recientes de tus animales para identificación si se pierden", essential: false },
      { text: "Si tienes caballos, practica la carga en remolque bajo presión - entrena regularmente", essential: false },
      { text: "Infórmate sobre los teléfonos de emergencias veterinarias de la zona", essential: false },
    ],
    during: [
      { text: "Evacúa ANTES de que sea obligatorio si ves humo cercano - no esperes la orden", essential: true, warning: true },
      { text: "NUNCA dejes a tus animales atrás - NO podrás volver a buscarlos", essential: true, warning: true },
      { text: "Cierra puertas y ventanas pero NO con llave - los bomberos necesitan acceder", essential: true },
      { text: "Si hay humo, mantén a los animales cerca del suelo donde el aire está más limpio", essential: true },
      { text: "Humedece toallas para cubrir narices de animales pequeños del humo", essential: true },
      { text: "Si quedas atrapado, ve a una habitación con ventana, moja toallas, señaliza", essential: true },
      { text: "NO intentes apagar el fuego si es grande - evacúa inmediatamente", essential: true, warning: true },
      { text: "Si tienes caballos y no puedes evacuarlos, suéltalos - tienen instinto de huida", essential: true },
      { text: "Lleva a los animales en transportín dentro del coche, no sueltos", essential: true },
      { text: "Conduce con las ventanillas cerradas y el aire en recirculación", essential: false },
    ],
    after: [
      { text: "NO regreses hasta autorización OFICIAL de bomberos - hay peligros ocultos", essential: true },
      { text: "Vigila problemas respiratorios: tos, dificultad para respirar, mucosidad", essential: true },
      { text: "El humo irrita ojos y vías respiratorias - consulta veterinario si hay síntomas", essential: true },
      { text: "Mantén a los animales alejados de zonas quemadas - cenizas tóxicas y brasas ocultas", essential: true, warning: true },
      { text: "Las cenizas pueden irritar patas - limpia bien después de cualquier exposición", essential: true },
      { text: "Ofrece un espacio tranquilo - los animales pueden estar muy estresados durante días", essential: true },
      { text: "Revisa que no hayan inhalado humo: encías pálidas o azuladas = urgencia veterinaria", essential: true },
      { text: "El agua de zonas quemadas puede estar contaminada - no dejes que beban", essential: true },
      { text: "Los animales perdidos pueden volver días después al olor familiar - deja comida/agua", essential: false },
    ],
    animalSpecific: {
      perros: ["Los perros con hocico corto (bulldogs, pugs) son más vulnerables al humo", "Pueden entrar en pánico y huir - correa siempre puesta"],
      gatos: ["Se esconden cuando tienen miedo - revisa TODOS los rincones antes de evacuar", "Usa transportín rígido, no bolsa de tela"],
      aves: ["Extremadamente sensibles al humo - evacúa primero si hay cualquier humo visible", "Cubre la jaula parcialmente para reducir estrés"],
      caballos: ["Si no puedes evacuar, márcalos con tu teléfono en el casco o crin", "No los ates - si el fuego llega, necesitan poder huir"],
      ganado: ["Abre puertas hacia zonas seguras y deja que huyan", "El instinto les llevará lejos del fuego"],
    }
  },

  terremoto: {
    before: [
      { text: "Identifica los puntos seguros de tu casa: marcos de puertas reforzados, mesas robustas", essential: true },
      { text: "Fija estanterías, muebles pesados y objetos que puedan caer a la pared", essential: true },
      { text: "Guarda la mochila de emergencia en un lugar accesible y conocido por todos", essential: true },
      { text: "Practica el protocolo DROP-COVER-HOLD: agáchate, cúbrete, agárrate", essential: true },
      { text: "Ten un silbato en cada mochila para pedir ayuda si quedas atrapado", essential: true },
      { text: "Identifica dónde se esconden tus animales cuando tienen miedo", essential: true },
      { text: "Ten una linterna con pilas en cada habitación principal", essential: true },
      { text: "Infórmate sobre cómo cortar gas, electricidad y agua de tu vivienda", essential: true },
      { text: "Si vives en zona sísmica, considera un refugio/caseta exterior para animales", essential: false },
      { text: "Practica llamar a tus animales con un sonido específico (silbido, palabra)", essential: false },
    ],
    during: [
      { text: "NO intentes agarrar a tu animal durante el temblor - PROTÉGETE TÚ PRIMERO", essential: true, warning: true },
      { text: "Agáchate, cúbrete bajo una mesa robusta y agárrate hasta que pare", essential: true },
      { text: "Aléjate de ventanas, espejos, cuadros y objetos que puedan caer", essential: true },
      { text: "Si estás fuera, aléjate de edificios, postes, árboles y cables eléctricos", essential: true },
      { text: "Si estás en coche, detente lejos de puentes, pasos elevados y estructuras", essential: true },
      { text: "NO uses ascensores - pueden quedar atrapados", essential: true, warning: true },
      { text: "Si estás en la cama, quédate y cúbrete la cabeza con la almohada", essential: true },
      { text: "El temblor dura segundos - resiste la urgencia de correr", essential: true },
      { text: "Si tienes acuario grande, aléjate - el agua puede derramarse y el cristal romperse", essential: false },
    ],
    after: [
      { text: "Busca INMEDIATAMENTE a tus animales - pueden estar escondidos y asustados", essential: true },
      { text: "Revisa si hay fugas de gas (olor) ANTES de encender luces o cerillas", essential: true, warning: true },
      { text: "Prepárate para réplicas - pueden ser casi tan fuertes como el terremoto inicial", essential: true },
      { text: "Revisa a tus animales por heridas, cortes de cristales o signos de shock", essential: true },
      { text: "Mantén la correa puesta - los animales muy asustados pueden huir", essential: true },
      { text: "Revisa tu vivienda por daños estructurales antes de quedarte dentro", essential: true },
      { text: "Si huele a gas, sal inmediatamente con animales y llama desde fuera", essential: true, warning: true },
      { text: "Los gatos pueden esconderse días - deja comida y no fuerces a salir", essential: true },
      { text: "No uses el teléfono excepto para emergencias reales - las líneas se saturan", essential: false },
      { text: "Limpia cristales rotos antes de dejar que los animales caminen", essential: false },
    ],
    animalSpecific: {
      perros: ["Pueden ladrar, temblar o intentar escapar - no los castigues, es miedo", "Mantén rutinas para darles seguridad"],
      gatos: ["Pueden esconderse DÍAS sin comer - es normal, deja comida accesible", "No los saques por la fuerza de su escondite"],
      aves: ["Pueden caer de perchas y herirse - revisa después del temblor", "El estrés puede causar que arranquen plumas"],
      peces: ["El movimiento puede sacar agua - ten preparado agua declorada", "Si el filtro falla, oxigena manualmente"],
      reptiles: ["Revisa que el terrario no tenga grietas que permitan escape", "Verifica que la fuente de calor funcione"],
    }
  },

  tsunami: {
    before: [
      { text: "Si vives en zona costera, conoce las rutas de evacuación a zonas altas (30m+)", essential: true },
      { text: "Un tsunami puede llegar 10-30 minutos después de un terremoto fuerte - actúa RÁPIDO", essential: true },
      { text: "Identifica edificios altos de hormigón como refugio si no puedes alejarte de la costa", essential: true },
      { text: "Ten la mochila de evacuación SIEMPRE lista con suministros para animales", essential: true },
      { text: "Practica la evacuación con tus animales - cada segundo cuenta", essential: true },
      { text: "Infórmate sobre las señales naturales: terremoto fuerte, mar que retrocede, ruido extraño", essential: true },
      { text: "Ten transportín y correa siempre accesibles cerca de la puerta", essential: true },
      { text: "Si tienes embarcación, conoce el protocolo de mar adentro (aguas profundas)", essential: false },
    ],
    during: [
      { text: "Si sientes un terremoto fuerte cerca de la costa, EVACÚA INMEDIATAMENTE a zonas altas", essential: true, warning: true },
      { text: "NO esperes a la alerta oficial - tu vida y la de tus animales dependen de actuar RÁPIDO", essential: true, warning: true },
      { text: "Corre hacia zonas altas (30m+) o edificios de hormigón de 3+ pisos", essential: true },
      { text: "Si ves el mar retroceder, HUYE INMEDIATAMENTE - es señal de tsunami inminente", essential: true, warning: true },
      { text: "Lleva a tus animales contigo - NO vuelvas a por ellos", essential: true },
      { text: "Si no puedes evacuar a tierra alta, sube al piso más alto de un edificio de hormigón", essential: true },
      { text: "Aléjate de ríos y canales - el tsunami puede subir por ellos", essential: true },
      { text: "El primer oleaje NO es necesariamente el más grande - quédate en alto", essential: true, warning: true },
    ],
    after: [
      { text: "NO bajes hasta que las autoridades confirmen que es seguro - pueden venir más olas", essential: true },
      { text: "Espera al menos 8-12 horas después de la última ola antes de regresar", essential: true },
      { text: "El agua de tsunami es extremadamente contaminada - no dejes que los animales la beban", essential: true, warning: true },
      { text: "Revisa a tus animales por hipotermia si tuvieron contacto con agua", essential: true },
      { text: "Los animales pueden haber tragado agua salada contaminada - vigila vómitos y diarrea", essential: true },
      { text: "Desinfecta todo lo que tocó el agua de tsunami", essential: true },
      { text: "Si perdiste a tu animal, regístralo inmediatamente en centros de evacuación", essential: false },
    ],
    animalSpecific: {
      perros: ["Pueden nadar pero se agotan rápido en corrientes - no confíes en su capacidad", "El agua salada causa deshidratación severa si la tragan"],
      gatos: ["Odian el agua - pueden entrar en pánico extremo", "Transportín imprescindible para evacuación rápida"],
      caballos: ["Si no puedes evacuarlos, suéltalos hacia zonas altas", "Son buenos nadadores pero no en corrientes de tsunami"],
    }
  },

  volcanico: {
    before: [
      { text: "Infórmate sobre el nivel de alerta volcánica de tu zona (verde, amarillo, naranja, rojo)", essential: true },
      { text: "Prepara mochila de evacuación con suministros para 72h+ y mascarillas N95/FFP2", essential: true },
      { text: "Ten gafas protectoras para ti y protección ocular para animales (si es posible)", essential: true },
      { text: "Identifica rutas de evacuación ALEJADAS de valles (por donde fluye la lava)", essential: true },
      { text: "Infórmate sobre refugios que admitan animales fuera de la zona de exclusión", essential: true },
      { text: "Ten preparados recipientes herméticos para agua y comida de animales", essential: true },
      { text: "La ceniza volcánica es tóxica - prepara espacio interior sellable para animales", essential: true },
      { text: "Infórmate sobre los síntomas de intoxicación por gases volcánicos (SO2, CO2)", essential: true },
      { text: "Si tienes ganado, identifica zonas de evacuación y acuerdos con otras fincas", essential: false },
    ],
    during: [
      { text: "Mantén a TODOS los animales dentro de casa con puertas y ventanas cerradas", essential: true },
      { text: "Sella rendijas con trapos húmedos para evitar entrada de ceniza", essential: true },
      { text: "Guarda agua y comida de animales en recipientes TAPADOS - la ceniza contamina todo", essential: true },
      { text: "La ceniza irrita los pulmones de los animales - minimiza su exposición", essential: true },
      { text: "Si debes evacuar, hazlo por rutas indicadas ALEJÁNDOTE de valles y cauces", essential: true, warning: true },
      { text: "NO te acerques a flujos de lava ni a zonas con gases volcánicos", essential: true, warning: true },
      { text: "Usa mascarilla N95 y protege los ojos - la ceniza es abrasiva", essential: true },
      { text: "La ceniza en el aire reduce visibilidad - conduce con precaución extrema", essential: true },
      { text: "Los gases volcánicos se acumulan en zonas bajas - mantén a animales en alto", essential: true, warning: true },
      { text: "Si la ceniza cae dentro de casa, límpiala con plumeros suaves SIN frotar", essential: false },
    ],
    after: [
      { text: "No regreses hasta que las autoridades declaren el fin de la emergencia", essential: true },
      { text: "La ceniza volcánica puede seguir cayendo días o semanas - mantén protección", essential: true },
      { text: "Revisa a tus animales por problemas respiratorios: tos, estornudos, dificultad", essential: true },
      { text: "Los animales que comieron ceniza pueden tener problemas digestivos - veterinario", essential: true },
      { text: "Limpia la ceniza de zonas donde estén los animales ANTES de dejarlos moverse", essential: true },
      { text: "El agua contaminada con ceniza puede causar problemas - usa agua embotellada", essential: true },
      { text: "Lava bien las patas de los animales - la ceniza es abrasiva y tóxica", essential: true },
      { text: "Los pastos contaminados con ceniza no son seguros para ganado", essential: true, warning: true },
      { text: "Revisa ojos y nariz de animales por irritación de ceniza", essential: false },
    ],
    animalSpecific: {
      perros: ["La ceniza irrita sus patas - usa protectores o limpia frecuentemente", "Los hocicos cortos (bulldogs) son más vulnerables a problemas respiratorios"],
      gatos: ["Se limpian lamiéndose - pueden ingerir ceniza tóxica", "Mantén en interior y limpia su pelaje con paño húmedo"],
      aves: ["Extremadamente sensibles a la ceniza y gases - prioridad de protección", "Mantén jaula tapada y alejada de ventanas"],
      ganado: ["Los pastos con ceniza pueden causar problemas dentales y digestivos", "Proporciona agua limpia y alimentación no contaminada"],
      caballos: ["El ejercicio con ceniza en el aire daña los pulmones - mantén en reposo", "Humedece el heno para reducir polvo"],
    }
  },

  huracan: {
    before: [
      { text: "Sigue los avisos meteorológicos con VARIOS DÍAS de antelación - prepárate antes", essential: true },
      { text: "Prepara suministros para 7 DÍAS - los huracanes pueden aislar zonas durante semanas", essential: true },
      { text: "Identifica una habitación interior SIN ventanas como refugio (baño interior, pasillo)", essential: true },
      { text: "Refuerza ventanas con contraventanas o tablas y asegura puertas", essential: true },
      { text: "Llena recipientes con agua potable (bañera para higiene, botellas para beber)", essential: true },
      { text: "Asegura objetos exteriores que puedan convertirse en proyectiles", essential: true },
      { text: "Si vives en zona de evacuación obligatoria, identifica refugio que acepte animales", essential: true },
      { text: "Ten medicamentos de animales para 2 semanas mínimo", essential: true },
      { text: "Congela agua en botellas - servirá para mantener fresca la comida si se va la luz", essential: false },
      { text: "Fotocopia documentos importantes y guárdalos en bolsa impermeable", essential: false },
    ],
    during: [
      { text: "Permanece en la habitación segura interior CON tus animales - no os separéis", essential: true },
      { text: "Aléjate de ventanas, puertas exteriores y claraboyas", essential: true, warning: true },
      { text: "Si el OJO del huracán pasa, NO SALGAS - la calma es temporal, volverá el viento", essential: true, warning: true },
      { text: "Mantén a los animales en transportín o con correa corta - pueden asustarse", essential: true },
      { text: "Ten radio a pilas para seguir instrucciones oficiales", essential: true },
      { text: "Si el viento es extremo, métete en un armario o bajo colchón con tus animales", essential: true },
      { text: "No uses velas - riesgo de incendio. Usa linternas", essential: true },
      { text: "Si el tejado comienza a ceder, refúgiate bajo estructura más sólida", essential: true, warning: true },
      { text: "Llena la bañera con agua de último momento si no lo hiciste antes", essential: false },
    ],
    after: [
      { text: "No salgas hasta que las autoridades confirmen que el huracán ha pasado completamente", essential: true },
      { text: "Cuidado con cables caídos, inundaciones y escombros - peligro de electrocución", essential: true, warning: true },
      { text: "Revisa que tus animales no hayan ingerido agua contaminada", essential: true },
      { text: "Los animales pueden estar muy estresados por el ruido - ofrece ambiente calmado", essential: true },
      { text: "No dejes que los animales caminen por zonas inundadas - contaminación y objetos cortantes", essential: true },
      { text: "Revisa tu vivienda por daños estructurales antes de moverte libremente", essential: true },
      { text: "Si evacuaste, no regreses hasta confirmación oficial", essential: true },
      { text: "Documenta daños con fotos para seguros antes de limpiar", essential: false },
    ],
    animalSpecific: {
      perros: ["El ruido del viento puede causarles pánico extremo - mantén cerca, habla calmado", "Pueden necesitar medicación ansiolítica - consulta veterinario antes de temporada"],
      gatos: ["Se esconderán en lugares pequeños - asegúrate de poder acceder a ellos", "El estrés puede causar que dejen de comer días"],
      aves: ["El estrés del ruido puede ser fatal - cubre jaula y mantén en zona tranquila", "Pueden necesitar días para recuperar comportamiento normal"],
    }
  },

  tornado: {
    before: [
      { text: "Identifica el refugio más seguro: sótano, habitación interior sin ventanas, bajo escaleras", essential: true },
      { text: "Ten preparada la mochila de emergencia con suministros para animales", essential: true },
      { text: "Infórmate sobre los signos de tornado: cielo verde-amarillo, granizo grande, calma súbita, rugido", essential: true },
      { text: "Ten un plan para reunir a los animales en segundos - practica regularmente", essential: true },
      { text: "Instala una app de alertas meteorológicas con notificaciones inmediatas", essential: true },
      { text: "Ten cascos o almohadas para proteger cabezas durante el tornado", essential: true },
      { text: "Identifica dónde se esconden tus animales - necesitas encontrarlos en segundos", essential: true },
      { text: "Ten una linterna y radio de pilas en el refugio designado", essential: false },
    ],
    during: [
      { text: "Tienes SEGUNDOS para actuar - ve al refugio INMEDIATAMENTE con tus animales", essential: true, warning: true },
      { text: "Baja al sótano o habitación interior sin ventanas en la planta más baja", essential: true },
      { text: "Aléjate de ventanas, puertas exteriores y esquinas de edificios", essential: true },
      { text: "Si no tienes sótano, métete en la bañera cubierto con colchón CON tu animal", essential: true },
      { text: "Protege tu cabeza y cuello con brazos, cojines o casco", essential: true },
      { text: "Pon a los animales pequeños en transportín CONTIGO en el refugio", essential: true },
      { text: "Si estás en coche, NO intentes huir del tornado - sal y busca zanja baja", essential: true, warning: true },
      { text: "NUNCA te refugies bajo un puente - actúa como embudo de viento", essential: true, warning: true },
      { text: "Si estás fuera, túmbate boca abajo en una zanja cubriéndote la cabeza", essential: true },
    ],
    after: [
      { text: "Espera a que pase completamente - pueden venir más tornados", essential: true },
      { text: "Busca a tus animales inmediatamente - pueden estar escondidos o heridos", essential: true },
      { text: "Cuidado con escombros, cristales, cables caídos y gas - peligros ocultos", essential: true, warning: true },
      { text: "Revisa a tus animales por heridas de objetos voladores o cristales", essential: true },
      { text: "No entres en edificios dañados con animales - pueden colapsar", essential: true },
      { text: "Los animales asustados pueden huir o atacar por pánico - acércate con calma", essential: true },
      { text: "Si tu animal escapó, marca tu ubicación y busca en refugios de emergencia", essential: true },
      { text: "El estrés post-tornado puede durar semanas - ofrece rutina y tranquilidad", essential: false },
    ],
    animalSpecific: {
      perros: ["El instinto les puede hacer huir - correa siempre puesta tras tornado", "Pueden estar en shock - movimientos suaves, voz calmada"],
      gatos: ["Buscarán el escondite más pequeño y oscuro - revisa todo", "El trauma puede causar cambios de comportamiento durante semanas"],
      animales_exterior: ["Si tienes animales fuera, necesitas un plan de refugio rápido para ellos", "Considera una estructura de refugio permanente en la propiedad"],
    }
  },

  nevada: {
    before: [
      { text: "Prepara mantas extra, camas térmicas y refugio cálido para animales de exterior", essential: true },
      { text: "Asegura suministro de comida y agua para 5-7 días - puedes quedar aislado", essential: true },
      { text: "Revisa que la calefacción funcione correctamente antes del frío", essential: true },
      { text: "Si tienes animales fuera, proporciona refugio aislado del suelo y protegido del viento", essential: true },
      { text: "El anticongelante es LETAL para animales - guárdalo seguro, limpia derrames inmediatamente", essential: true, warning: true },
      { text: "Ten comida extra para animales - necesitan más calorías en frío extremo", essential: true },
      { text: "Revisa las patas y almohadillas de perros - aplica vaselina o protector", essential: true },
      { text: "Ten preparado un plan si se va la electricidad (mantas, calentadores portátiles seguros)", essential: true },
      { text: "Identifica señales de hipotermia: temblores, letargo, encías pálidas", essential: false },
    ],
    during: [
      { text: "Mete a TODOS los animales dentro de casa - ningún animal debe quedarse fuera", essential: true, warning: true },
      { text: "Si un animal muestra hipotermia, caliéntalo GRADUALMENTE con mantas secas", essential: true },
      { text: "Ofrece más comida de lo normal - necesitan más calorías para mantener calor corporal", essential: true },
      { text: "Limpia la sal y productos químicos de las patas después de cada paseo", essential: true },
      { text: "Los paseos deben ser cortos - riesgo de congelación de almohadillas", essential: true },
      { text: "Vigila que no coman nieve - puede causar hipotermia interna", essential: true },
      { text: "Los perros pequeños y de pelo corto necesitan abrigo incluso para salir", essential: true },
      { text: "Mantén el agua de bebida sin congelar - usa cuencos térmicos si es necesario", essential: true },
      { text: "No dejes animales en el coche - se convierte en refrigerador", essential: true, warning: true },
      { text: "Golpea el capó del coche antes de arrancar - los gatos se refugian en el motor", essential: false },
    ],
    after: [
      { text: "Revisa las patas de tus animales por grietas, cortes o heridas del hielo", essential: true },
      { text: "Continúa ofreciendo más comida mientras haga frío extremo", essential: true },
      { text: "Los primeros paseos tras nevada: cuidado con hielo oculto bajo nieve", essential: true },
      { text: "Si usaste sal en tu entrada, enjuaga bien las patas de los animales", essential: true },
      { text: "La nieve derretida puede revelar basura y objetos peligrosos - vigila", essential: false },
    ],
    animalSpecific: {
      perros: ["Los perros nórdicos (huskies, malamutes) toleran mejor pero también tienen límites", "Los perros mayores, cachorros y enfermos son más vulnerables al frío"],
      gatos: ["Prefieren el calor - asegura acceso a zonas cálidas de la casa", "Los gatos de exterior necesitan refugio aislado con entrada pequeña (conserva calor)"],
      pequeños_mamiferos: ["Conejos, cobayas: muy sensibles al frío, mantén en interior", "Necesitan heno extra para generar calor al digerir"],
      aves: ["Muy sensibles a cambios de temperatura - evita corrientes de aire", "Algunas especies tropicales pueden necesitar calefacción adicional"],
      reptiles: ["Absolutamente crítico mantener temperatura - ten fuente de calor de respaldo", "Sin calor adecuado pueden morir en horas"],
    }
  },

  ola_calor: {
    before: [
      { text: "Instala o revisa aire acondicionado/ventiladores - ten un plan si falla la electricidad", essential: true },
      { text: "Identifica las zonas más frescas de tu casa para refugio de animales", essential: true },
      { text: "Almacena agua extra - la demanda aumenta mucho en olas de calor", essential: true },
      { text: "Prepara bolsas de hielo o toallas húmedas para enfriar a los animales", essential: true },
      { text: "Infórmate sobre los signos de golpe de calor: jadeo excesivo, babeo, desorientación, encías rojas", essential: true },
      { text: "Adapta los horarios de paseo a primera hora de la mañana y última de la noche", essential: true },
      { text: "Verifica que los animales de exterior tengan sombra PERMANENTE todo el día", essential: true },
      { text: "Ten el teléfono de urgencias veterinarias a mano - el golpe de calor es emergencia", essential: true },
      { text: "Rapa o recorta el pelo de perros muy peludos (pero deja algo para proteger del sol)", essential: false },
    ],
    during: [
      { text: "NUNCA dejes animales en el coche - en 10 minutos puede ser mortal", essential: true, warning: true },
      { text: "Asegura acceso CONSTANTE a agua fresca y limpia - rellena varias veces al día", essential: true },
      { text: "Evita paseos en las horas centrales del día (11:00-19:00)", essential: true },
      { text: "Comprueba el asfalto con tu mano: si quema, quema las patas de tu perro", essential: true },
      { text: "Vigila signos de golpe de calor: jadeo excesivo, babeo, desorientación, vómitos", essential: true, warning: true },
      { text: "Ofrece helados caseros para perros (caldo congelado, fruta congelada)", essential: true },
      { text: "Humedece toallas y ponlas sobre el animal para refrescar (no agua helada)", essential: true },
      { text: "Los animales braquicéfalos (bulldog, pug, persa) son muy vulnerables - cuidado extremo", essential: true, warning: true },
      { text: "Si tu animal muestra golpe de calor: agua fresca (NO helada), sombra, veterinario URGENTE", essential: true, warning: true },
      { text: "Proporciona piscinas pequeñas o fuentes para que se refresquen", essential: false },
    ],
    after: [
      { text: "Mantén vigilancia de hidratación hasta que las temperaturas bajen", essential: true },
      { text: "Restablece gradualmente las rutinas de ejercicio cuando refresque", essential: true },
      { text: "Si hubo golpe de calor, sigue el tratamiento veterinario estrictamente", essential: true },
      { text: "Los animales que sufrieron golpe de calor son más susceptibles en el futuro", essential: true },
      { text: "Revisa que todos los animales se hayan recuperado bien antes de volver a la rutina", essential: false },
    ],
    animalSpecific: {
      perros: ["Razas braquicéfalas (bulldog, pug): MÁXIMO riesgo, minimiza cualquier esfuerzo", "Los perros negros absorben más calor - más cuidado"],
      gatos: ["Suelen regular bien pero vigila gatos persas y mayores", "Deja acceso a zonas frescas (baldosas de baño, sótano)"],
      conejos: ["MUY sensibles al calor - pueden morir sobre 28°C", "Proporciona baldosas frías, botellas de agua congelada"],
      aves: ["Pueden sufrir golpe de calor - coloca jaula en zona fresca", "Ofrece baño de agua para que se refresquen"],
      reptiles: ["Aunque son de sangre fría, el exceso de calor es peligroso", "Asegura que tengan zona fresca en el terrario"],
    }
  },

  sequia: {
    before: [
      { text: "Almacena agua suficiente para tus animales - mínimo 2 semanas de reserva", essential: true },
      { text: "Identifica fuentes alternativas de agua potable (manantiales, tiendas, vecinos)", essential: true },
      { text: "Adapta los horarios de actividad para evitar las horas de más calor", essential: true },
      { text: "Asegura sombra suficiente para animales de exterior durante todo el día", essential: true },
      { text: "Reduce el número de animales si no puedes garantizar agua y comida suficiente", essential: true },
      { text: "Infórmate sobre el consumo de agua de tus animales para calcular reservas", essential: true },
      { text: "Si tienes ganado, considera vender antes de que la sequía empeore", essential: false },
    ],
    during: [
      { text: "Asegura acceso constante a agua fresca y limpia - prioridad absoluta", essential: true },
      { text: "Evita cualquier ejercicio intenso en las horas de calor", essential: true },
      { text: "Vigila signos de golpe de calor: jadeo excesivo, desorientación, encías secas", essential: true, warning: true },
      { text: "NUNCA dejes animales en el coche, ni con ventanas abiertas", essential: true, warning: true },
      { text: "Ofrece alimentos húmedos para aumentar la ingesta de agua", essential: true },
      { text: "Proporciona múltiples puntos de agua en la casa y jardín", essential: true },
      { text: "Los animales de exterior necesitan agua varias veces al día - se evapora rápido", essential: true },
      { text: "Si el agua del grifo tiene restricciones, prioriza a los animales", essential: true },
      { text: "Reduce la ración de comida seca - aumenta la necesidad de agua", essential: false },
    ],
    after: [
      { text: "Continúa vigilando la hidratación hasta que se normalice el suministro de agua", essential: true },
      { text: "Restablece gradualmente las rutinas de ejercicio cuando haya agua suficiente", essential: true },
      { text: "Los animales deshidratados pueden tardar días en recuperarse - paciencia", essential: true },
      { text: "Si compraste agua embotellada, mantén reserva para futuras sequías", essential: false },
    ],
    animalSpecific: {
      perros: ["Necesitan aproximadamente 50-100ml de agua por kg de peso al día", "El jadeo aumenta la pérdida de agua - evita ejercicio"],
      gatos: ["Tienden a beber poco - ofrece fuentes de agua corriente que les atraen más", "Los alimentos húmedos ayudan mucho a la hidratación"],
      conejos: ["Muy sensibles a la deshidratación - vigila que beban regularmente", "Ofrece verduras con alto contenido de agua"],
      aves: ["Bañarse les ayuda a regular temperatura - ofrece baño diario", "Vigila que el agua no se caliente demasiado"],
      ganado: ["Una vaca necesita 50-100 litros de agua al día", "La producción de leche cae drásticamente sin agua suficiente"],
    }
  },

  tecnologico: {
    before: [
      { text: "Infórmate sobre si vives cerca de industrias químicas, centrales nucleares o rutas de transporte de mercancías peligrosas", essential: true },
      { text: "Ten preparada mochila de evacuación con suministros para 72h mínimo", essential: true },
      { text: "Infórmate sobre las rutas de evacuación de tu zona - suelen estar definidas por Protección Civil", essential: true },
      { text: "Ten mascarillas (N95/FFP2 mínimo) y gafas de protección para ti", essential: true },
      { text: "Identifica una habitación interior que puedas sellar (sin ventanas o con pocas)", essential: true },
      { text: "Ten cinta adhesiva ancha y plástico para sellar ventanas y puertas", essential: true },
      { text: "Infórmate sobre los símbolos de mercancías peligrosas en camiones y trenes", essential: true },
      { text: "Ten radio a pilas para recibir instrucciones oficiales", essential: true },
      { text: "Infórmate sobre el plan de emergencia de tu municipio para accidentes industriales", essential: false },
    ],
    during: [
      { text: "Si ves humo, nube extraña o explosion industrial: ALÉJATE inmediatamente", essential: true, warning: true },
      { text: "Si las autoridades ordenan CONFINAMIENTO: cierra todo, sella con plástico y cinta", essential: true },
      { text: "Lleva a los animales a una habitación interior SIN ventanas", essential: true },
      { text: "Si es posible, lleva a los animales a RECINTOS CERRADOS (Protección Civil España)", essential: true },
      { text: "Apaga aire acondicionado, ventilación y cualquier entrada de aire exterior", essential: true },
      { text: "NO salgas hasta que las autoridades lo indiquen - los gases pueden ser invisibles", essential: true, warning: true },
      { text: "Si hueles algo extraño o te sientes mal, cúbrete nariz y boca con paño húmedo", essential: true },
      { text: "En accidente nuclear: cierra ventanas, quítate ropa exterior al entrar, dúchate", essential: true },
      { text: "Sigue SOLO las instrucciones oficiales - ignora rumores", essential: true },
      { text: "No uses el teléfono excepto emergencias - las líneas se saturan", essential: false },
    ],
    after: [
      { text: "NO salgas hasta autorización OFICIAL - pueden quedar sustancias peligrosas", essential: true },
      { text: "Si estuviste expuesto, quítate la ropa exterior y dúchate antes de tocar a los animales", essential: true },
      { text: "Vigila a tus animales por síntomas: dificultad respiratoria, vómitos, temblores", essential: true },
      { text: "Si tus animales estuvieron expuestos a contaminantes, lava bien su pelaje", essential: true },
      { text: "No consumas alimentos ni agua que puedan haber sido contaminados", essential: true, warning: true },
      { text: "No dejes que los animales coman hierba, plantas o beban agua de exterior", essential: true, warning: true },
      { text: "Sigue las indicaciones sobre zonas seguras para paseos", essential: true },
      { text: "En accidente nuclear, puede haber restricciones prolongadas - sigue informado", essential: true },
      { text: "Si hay contaminación del suelo, los animales que salen al exterior necesitan limpieza al volver", essential: false },
    ],
    animalSpecific: {
      perros: ["Si hubo exposición química, lava bien con agua y jabón neutro", "No dejes que laman el suelo o vegetación hasta confirmación de seguridad"],
      gatos: ["Se limpian lamiéndose - si hubo contaminación, limpia su pelo antes de que se acicalen", "Mantén en interior hasta confirmación de seguridad"],
      aves: ["Extremadamente sensibles a gases y contaminantes - prioridad en sellado de habitación", "Si hay síntomas respiratorios, ventila con aire limpio cuando sea seguro"],
      ganado: ["No consumas leche ni carne hasta que las autoridades lo autoricen", "Los pastos pueden estar contaminados durante tiempo prolongado"],
    }
  },

  epidemia: {
    before: [
      { text: "Mantén TODAS las vacunas de tus animales al día - es la mejor prevención", essential: true },
      { text: "Infórmate sobre los síntomas de las enfermedades comunes en tu zona y especie", essential: true },
      { text: "Ten el contacto de tu veterinario y urgencias veterinarias siempre a mano", essential: true },
      { text: "Mantén buena higiene: limpia regularmente comederos, bebederos y camas", essential: true },
      { text: "Evita contacto con animales desconocidos o de origen dudoso", essential: true },
      { text: "Si adoptas un nuevo animal, cuarentena de 2 semanas antes de juntarlo", essential: true },
      { text: "Almacena medicamentos básicos y desinfectantes recomendados por tu veterinario", essential: false },
      { text: "Infórmate sobre los protocolos de las enfermedades de declaración obligatoria", essential: false },
    ],
    during: [
      { text: "Aísla INMEDIATAMENTE a los animales enfermos del resto", essential: true },
      { text: "Limita el contacto con otros animales: nada de parques, paseos compartidos, guarderías", essential: true },
      { text: "Desinfecta tus manos y ropa después de tocar animales enfermos ANTES de tocar otros", essential: true },
      { text: "Sigue ESTRICTAMENTE las indicaciones veterinarias - no improvises", essential: true },
      { text: "NO automediques - algunos medicamentos humanos son tóxicos para animales", essential: true, warning: true },
      { text: "Usa utensilios separados para animales enfermos (comederos, bebederos, juguetes)", essential: true },
      { text: "Si hay alerta sanitaria oficial, sigue las restricciones de movimiento", essential: true },
      { text: "Informa a tu veterinario de cualquier síntoma - algunas enfermedades son de declaración obligatoria", essential: true },
      { text: "Mantén registro de síntomas, evolución y tratamientos", essential: false },
    ],
    after: [
      { text: "Mantén la cuarentena el tiempo COMPLETO indicado por el veterinario", essential: true },
      { text: "Desinfecta A FONDO todos los espacios, objetos y textiles del animal enfermo", essential: true },
      { text: "Reintroduce gradualmente el contacto con otros animales", essential: true },
      { text: "Algunos virus persisten en el ambiente - desinfecta con productos adecuados", essential: true },
      { text: "Si el animal falleció, sigue protocolos de eliminación recomendados (consulta veterinario)", essential: true },
      { text: "Vigila a los otros animales por síntomas durante 2-4 semanas", essential: true },
      { text: "Actualiza el calendario de vacunación si corresponde", essential: false },
    ],
    animalSpecific: {
      perros: ["Parvovirus, moquillo, leptospirosis: vacunas esenciales", "La tos de las perreras se transmite muy fácil - aísla al primer síntoma"],
      gatos: ["Panleucopenia, calicivirus, leucemia felina: vacunas clave", "Los gatos pueden ser portadores asintomáticos - cuidado al introducir nuevos"],
      aves: ["Psitacosis, gripe aviar: algunas son zoonosis (transmisibles a humanos)", "Cuarentena de nuevas aves es crítica"],
      conejos: ["Mixomatosis, enfermedad hemorrágica: vacunas anuales obligatorias en muchas zonas", "Muy vulnerables - mortalidad alta en brotes"],
      ganado: ["Lengua azul, fiebre aftosa, brucelosis: declaración obligatoria", "Sigue protocolos oficiales al pie de la letra"],
    }
  },
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
export default function EmergencyPlansPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState(searchParams.get("type") || null);
  const [activePhase, setActivePhase] = useState("before");
  const [completedItems, setCompletedItems] = useState({});
  const [savedPlans, setSavedPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAnimalTips, setShowAnimalTips] = useState(false);
  const [showEmotionalTips, setShowEmotionalTips] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    loadSavedPlans();
  }, [user, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem(`emergency_plan_progress_${selectedType}`);
    if (saved) {
      setCompletedItems(JSON.parse(saved));
    } else {
      setCompletedItems({});
    }
  }, [selectedType]);

  const loadSavedPlans = async () => {
    try {
      const token = localStorage.getItem("ea_token");
      const res = await axios.get(`${API}/prepara/plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedPlans(res.data || []);
    } catch (error) {
      console.error("Error loading plans:", error);
    }
  };

  const toggleItem = (phase, index) => {
    const key = `${phase}_${index}`;
    const newCompleted = {
      ...completedItems,
      [key]: !completedItems[key],
    };
    setCompletedItems(newCompleted);
    localStorage.setItem(`emergency_plan_progress_${selectedType}`, JSON.stringify(newCompleted));
  };

  const getPhaseProgress = (phase) => {
    const guide = EMERGENCY_GUIDES[selectedType];
    if (!guide || !guide[phase]) return 0;
    const total = guide[phase].length;
    const completed = guide[phase].filter((_, idx) => completedItems[`${phase}_${idx}`]).length;
    return Math.round((completed / total) * 100);
  };

  const getTotalProgress = () => {
    const guide = EMERGENCY_GUIDES[selectedType];
    if (!guide) return 0;
    let total = 0;
    let completed = 0;
    ["before", "during", "after"].forEach((phase) => {
      if (guide[phase]) {
        total += guide[phase].length;
        completed += guide[phase].filter((_, idx) => completedItems[`${phase}_${idx}`]).length;
      }
    });
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const savePlan = async () => {
    if (!selectedType) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("ea_token");
      const emergencyType = EMERGENCY_TYPES.find((t) => t.id === selectedType);
      
      await axios.post(
        `${API}/prepara/plans`,
        {
          name: `Plan ${emergencyType?.label}`,
          emergency_type: selectedType,
          progress: completedItems,
          total_progress: getTotalProgress(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Plan guardado correctamente");
      loadSavedPlans();
    } catch (error) {
      console.error("Error saving plan:", error);
      toast.error("Error al guardar el plan");
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // EXPORTAR A PDF - Genera documento imprimible
  // ============================================
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  
  const exportToPDF = async () => {
    try {
      if (!selectedType || !guide) {
        toast.error("Selecciona un tipo de emergencia primero");
        return;
      }
      
      const emergencyType = EMERGENCY_TYPES.find((t) => t.id === selectedType);
      
      toast.info("Generando PDF...");
      
      // Preparar datos para el backend
      const pdfData = {
        emergency_type: selectedType,
        plan_label: emergencyType?.label || 'Plan',
        plan_description: emergencyType?.description || '',
        completed_items: completedItems,
        before_items: guide.before || [],
        during_items: guide.during || [],
        after_items: guide.after || [],
        animal_specific: guide.animalSpecific || {}
      };
      
      // Hacer petición al backend
      const response = await fetch(`${API}/prepara/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pdfData)
      });
      
      if (!response.ok) {
        throw new Error(`Error del servidor: ${response.status}`);
      }
      
      // Obtener el blob del PDF
      const blob = await response.blob();
      
      // Método original que SÍ funcionó: crear link y click
      const safeLabel = normalizeForPDF(emergencyType?.label || 'Plan').replace(/\s+/g, '_');
      const fileName = `Plan_${safeLabel}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      // También mostrar el modal con la vista previa
      setPdfDataUrl(URL.createObjectURL(blob));
      setPdfModalOpen(true);
      
      toast.success("PDF generado correctamente");
    } catch (error) {
      console.error("Error al generar PDF:", error);
      toast.error(`Error al generar el PDF: ${error.message}`);
    }
  };
  
  const downloadPdfFromModal = async () => {
    // Regenerar el PDF y forzar descarga
    try {
      const emergencyType = EMERGENCY_TYPES.find((t) => t.id === selectedType);
      
      const pdfData = {
        emergency_type: selectedType,
        plan_label: emergencyType?.label || 'Plan',
        plan_description: emergencyType?.description || '',
        completed_items: completedItems,
        before_items: guide?.before || [],
        during_items: guide?.during || [],
        after_items: guide?.after || [],
        animal_specific: guide?.animalSpecific || {}
      };
      
      const response = await fetch(`${API}/prepara/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pdfData)
      });
      
      const blob = await response.blob();
      const safeLabel = normalizeForPDF(emergencyType?.label || 'Plan').replace(/\s+/g, '_');
      const fileName = `Plan_${safeLabel}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Descarga iniciada");
    } catch (error) {
      toast.error("Error al descargar");
    }
  };
  
  const closePdfModal = () => {
    setPdfModalOpen(false);
    if (pdfDataUrl) {
      URL.revokeObjectURL(pdfDataUrl);
      setPdfDataUrl(null);
    }
  };

  const printPlan = () => {
    window.print();
  };

  if (!user) return null;

  const selectedEmergency = EMERGENCY_TYPES.find((t) => t.id === selectedType);
  const guide = selectedType ? EMERGENCY_GUIDES[selectedType] : null;

  return (
    <div className="min-h-screen" data-testid="emergency-plans-page" data-page="emergency" style={{ background: C.white, fontFamily: BODY }}>
      {/* Header */}
      <header className="sticky top-0 z-50 print:hidden" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/prepara/panel")}
                className="flex items-center gap-2 text-sm font-semibold hover:opacity-80"
                style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}
                data-testid="back-to-panel-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Panel
              </button>
              <div className="h-5 w-px" style={{ background: "rgba(255,255,255,0.15)" }} />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center" style={{ background: C.olive, borderRadius: 4 }}>
                  <BookOpen className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h1 className="text-sm font-bold" style={{ fontFamily: BODY, color: C.white }}>Planes de emergencia</h1>
                  <p className="text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: MONO, color: C.olive }}>13 tipos de emergencia</p>
                </div>
              </div>
            </div>

            {selectedType && (
              <div className="flex items-center gap-2">
                <button
                  onClick={exportToPDF}
                  className="px-3 py-1.5 text-[10px] font-semibold tracking-wider flex items-center gap-1.5 hover:-translate-y-0.5 transition-all"
                  style={{ fontFamily: MONO, color: C.olive, border: `1px solid ${C.olive}60`, borderRadius: 4, background: "none", cursor: "pointer" }}
                  data-testid="download-pdf-btn"
                >
                  <FileDown className="w-3.5 h-3.5" />
                  PDF
                </button>
                <button
                  onClick={printPlan}
                  className="px-3 py-1.5 text-[10px] font-semibold tracking-wider flex items-center gap-1.5 hover:-translate-y-0.5 transition-all"
                  style={{ fontFamily: MONO, color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 4, background: "none", cursor: "pointer" }}
                  data-testid="print-plan-btn"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Imprimir
                </button>
                <button
                  onClick={savePlan}
                  disabled={saving}
                  className="px-4 py-1.5 text-[10px] font-semibold tracking-wider flex items-center gap-1.5 hover:-translate-y-0.5 transition-all disabled:opacity-50"
                  style={{ fontFamily: MONO, background: C.olive, color: C.white, borderRadius: 4, border: "none", cursor: "pointer" }}
                  data-testid="save-plan-btn"
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {!selectedType ? (
          /* Emergency Type Selection */
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* Intro */}
            <div className="mb-8 p-6 flex items-start gap-4" style={{ borderLeft: `3px solid ${C.olive}`, background: `${C.olive}06` }}>
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: C.olive, borderRadius: 4 }}>
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold mb-2" style={{ fontFamily: DISPLAY, color: C.navy }}>
                  Guías completas de emergencia
                </h2>
                <p className="text-sm leading-relaxed" style={{ fontFamily: BODY, color: C.gray }}>
                  Basadas en <strong>60+ documentos oficiales</strong> de Ready.gov, FEMA, AVMA, Cruz Roja, 
                  Protección Civil de España y protocolos de Latinoamérica. Guías paso a paso sobre qué hacer{" "}
                  <strong>antes</strong>, <strong>durante</strong> y <strong>después</strong> de cada emergencia,
                  con consejos específicos por tipo de animal.
                </p>
              </div>
            </div>

            {/* Emergency Types Grid */}
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: B.font, color: "#2D3561" }}>
              Selecciona un tipo de emergencia
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {EMERGENCY_TYPES.map((type, idx) => (
                <motion.div
                  key={type.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                >
                  <div
                    className="cursor-pointer transition-all hover:-translate-y-1 h-full"
                    onClick={() => setSelectedType(type.id)}
                    data-testid={`emergency-type-${type.id}`}
                    style={{ border: B.border, boxShadow: B.shadowSm, background: "#fff", borderTop: `4px solid ${type.color}` }}
                  >
                    <div className="p-4 text-center">
                      <span className="text-3xl mb-2 block">{type.icon}</span>
                      <h3 className="text-sm font-bold mb-1 leading-tight" style={{ fontFamily: B.font, color: "#2D3561" }}>
                        {type.label}
                      </h3>
                      <p className="text-[10px] leading-tight" style={{ fontFamily: B.mono, color: "#2D356188" }}>{type.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Saved Plans */}
            {savedPlans.length > 0 && (
              <div className="mt-12">
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: B.font, color: "#2D3561" }}>
                  Tus planes guardados
                </h2>
                <div className="grid gap-3">
                  {savedPlans.map((plan) => {
                    const type = EMERGENCY_TYPES.find((t) => t.id === plan.emergency_type);
                    return (
                      <div
                        key={plan.plan_id}
                        className="cursor-pointer transition-all hover:-translate-y-0.5"
                        onClick={() => setSelectedType(plan.emergency_type)}
                        data-testid={`saved-plan-${plan.emergency_type}`}
                        style={{ border: B.border, background: "#fff", borderLeft: `6px solid ${type?.color || "#8B9A2D"}` }}
                      >
                        <div className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className="text-2xl">{type?.icon || ""}</span>
                            <div>
                              <h3 className="text-base font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                                {plan.name}
                              </h3>
                              <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356188" }}>
                                {plan.total_progress || 0}% completado
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-24 h-2.5" style={{ background: "#2D356115", border: "2px solid #2D3561" }}>
                              <div className="h-full" style={{ width: `${plan.total_progress || 0}%`, background: "#686af3" }} />
                            </div>
                            <ChevronRight className="w-5 h-5" style={{ color: "#2D356144" }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          /* Emergency Guide View */
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Back button */}
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedType(null);
                  setShowAnimalTips(false);
                  setShowEmotionalTips(false);
                }}
                className="text-slate-500 hover:text-[#8B9A2D]"
                data-testid="back-to-types-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Todos los planes
              </Button>
            </div>

            {/* Emergency Header */}
            <div className="mb-6 overflow-hidden" style={{ border: B.border, boxShadow: B.shadow, background: "#fff" }}>
              <div className="h-3" style={{ backgroundColor: selectedEmergency?.color }} />
              <div className="p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <span className="text-5xl">{selectedEmergency?.icon}</span>
                    <div>
                      <h2 className="text-2xl font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                        Plan: {selectedEmergency?.label}
                      </h2>
                      <p className="text-sm" style={{ color: "#2D356188" }}>
                        {selectedEmergency?.description}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold" style={{ fontFamily: B.font, color: selectedEmergency?.color }}>
                      {getTotalProgress()}%
                    </p>
                    <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356188" }}>preparado</p>
                  </div>
                </div>

                {/* Phase Progress */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  {[
                    { id: "before", label: "Antes", icon: Clock },
                    { id: "during", label: "Durante", icon: AlertTriangle },
                    { id: "after", label: "Después", icon: CheckCircle },
                  ].map((phase) => (
                    <div
                      key={phase.id}
                      className="p-3 text-center cursor-pointer transition-all hover:-translate-y-0.5"
                      onClick={() => setActivePhase(phase.id)}
                      data-testid={`phase-${phase.id}`}
                      style={{
                        border: activePhase === phase.id ? B.border : "2px solid #2D356133",
                        background: activePhase === phase.id ? "#8B9A2D15" : "#f8f7f4",
                        boxShadow: activePhase === phase.id ? B.shadowSm : "none",
                      }}
                    >
                      <phase.icon className="w-5 h-5 mx-auto mb-1" style={{ color: activePhase === phase.id ? "#8B9A2D" : "#2D356166" }} />
                      <p className="text-sm font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                        {phase.label}
                      </p>
                      <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356188" }}>
                        {getPhaseProgress(phase.id)}%
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Emotional Tips Card */}
            <div className="mb-4" style={{ border: B.border, background: "#fff", borderLeft: "6px solid #a88efb" }}>
              <div className="p-4">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setShowEmotionalTips(!showEmotionalTips)}
                  data-testid="toggle-emotional-tips"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center" style={{ background: "#a88efb", border: "2px solid #2D3561" }}>
                      <Heart className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                        {EMOTIONAL_TIPS[activePhase]?.title}
                      </h3>
                      <p className="text-xs" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                        Apoyo emocional para ti y tus animales
                      </p>
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 transition-transform ${showEmotionalTips ? "rotate-90" : ""}`} style={{ color: "#a88efb" }} />
                </div>
                {showEmotionalTips && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-4 pt-4"
                    style={{ borderTop: "2px solid #a88efb33" }}
                  >
                    <ul className="space-y-2">
                      {EMOTIONAL_TIPS[activePhase]?.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: "#2D3561" }}>
                          <span style={{ color: "#a88efb" }}>—</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Animal Specific Tips */}
            {guide?.animalSpecific && (
              <div className="mb-4" style={{ border: B.border, background: "#fff", borderLeft: "6px solid #f0a030" }}>
                <div className="p-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setShowAnimalTips(!showAnimalTips)}
                    data-testid="toggle-animal-tips"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="text-sm font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                          Consejos por tipo de animal
                        </h3>
                        <p className="text-xs" style={{ fontFamily: B.mono, color: "#f0a030" }}>
                          {Object.keys(guide.animalSpecific).length} tipos: {Object.keys(guide.animalSpecific).join(", ")}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${showAnimalTips ? "rotate-90" : ""}`} style={{ color: "#f0a030" }} />
                  </div>
                  {showAnimalTips && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-4 pt-4 space-y-4"
                      style={{ borderTop: "2px solid #f0a03033" }}
                    >
                      {Object.entries(guide.animalSpecific).map(([animal, tips]) => (
                        <div key={animal}>
                          <h4 className="text-sm font-bold capitalize mb-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
                            {animal.replace(/_/g, " ")}
                          </h4>
                          <ul className="space-y-1">
                            {tips.map((tip, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: "#2D3561" }}>
                                <span style={{ color: "#f0a030" }}>—</span>
                                <span>{tip}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </div>
              </div>
            )}

            {/* Phase Content */}
            <div style={{ border: B.border, background: "#fff", boxShadow: B.shadow }}>
              <div className="p-6" style={{ borderBottom: "2px solid #2D3561" }}>
                <div className="flex items-center gap-3">
                  {activePhase === "before" && <Clock className="w-6 h-6" style={{ color: "#686af3" }} />}
                  {activePhase === "during" && <AlertTriangle className="w-6 h-6" style={{ color: "#f64e0e" }} />}
                  {activePhase === "after" && <CheckCircle className="w-6 h-6" style={{ color: "#8B9A2D" }} />}
                  <div>
                    <h3 className="text-lg font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                      {activePhase === "before" && "Antes de la emergencia"}
                      {activePhase === "during" && "Durante la emergencia"}
                      {activePhase === "after" && "Después de la emergencia"}
                    </h3>
                    <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356188" }}>
                      {activePhase === "before" && "Prepárate con antelación para minimizar riesgos"}
                      {activePhase === "during" && "Acciones críticas para proteger a tus animales"}
                      {activePhase === "after" && "Pasos para la recuperación y normalización"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {guide?.[activePhase]?.map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.02 }}
                      className="flex items-start gap-3 p-4 transition-all"
                      style={{
                        background: completedItems[`${activePhase}_${idx}`] ? "#f0fff4" : item.warning ? "#fff4f4" : "#fff",
                        border: `2px solid ${completedItems[`${activePhase}_${idx}`] ? "#8B9A2D" : item.warning ? "#e8637a" : "#2D356133"}`,
                      }}
                      data-testid={`checklist-item-${activePhase}-${idx}`}
                    >
                      <Checkbox
                        checked={completedItems[`${activePhase}_${idx}`] || false}
                        onCheckedChange={() => toggleItem(activePhase, idx)}
                        className="mt-0.5 data-[state=checked]:bg-[#8B9A2D] data-[state=checked]:border-[#8B9A2D]"
                      />
                      <div className="flex-1">
                        <p className={`text-sm ${completedItems[`${activePhase}_${idx}`] ? "line-through" : ""}`}
                          style={{ fontFamily: B.font, color: completedItems[`${activePhase}_${idx}`] ? "#2D356166" : "#2D3561" }}>
                          {item.text}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {item.essential && (
                            <span className="text-[10px] font-bold px-2 py-0.5" style={{ fontFamily: B.mono, background: "#f0a030", color: "#fff", border: "1px solid #2D3561" }}>
                              Esencial
                            </span>
                          )}
                          {item.warning && (
                            <span className="text-[10px] font-bold px-2 py-0.5" style={{ fontFamily: B.mono, background: "#e8637a", color: "#fff", border: "1px solid #2D3561" }}>
                              Importante
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Phase Navigation */}
                <div className="flex justify-between mt-8 pt-6" style={{ borderTop: "2px solid #2D356122" }}>
                  <button
                    onClick={() => {
                      if (activePhase === "during") setActivePhase("before");
                      if (activePhase === "after") setActivePhase("during");
                    }}
                    disabled={activePhase === "before"}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-30 transition-all hover:-translate-y-0.5"
                    style={{ fontFamily: B.mono, color: "#2D3561", border: "2px solid #2D3561" }}
                    data-testid="prev-phase-btn"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Fase anterior
                  </button>
                  <button
                    onClick={() => {
                      if (activePhase === "before") setActivePhase("during");
                      if (activePhase === "during") setActivePhase("after");
                    }}
                    disabled={activePhase === "after"}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-30 transition-all hover:-translate-y-0.5"
                    style={{ fontFamily: B.mono, background: "#8B9A2D", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                    data-testid="next-phase-btn"
                  >
                    Siguiente fase
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Info Card */}
            <Card className="mt-6 bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Info className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-bold text-blue-900 mb-1">
                      Recuerda
                    </h3>
                    <p className="font-body text-sm text-blue-700">
                      Tu preparación puede marcar la diferencia. Revisa este plan regularmente, 
                      practica las evacuaciones con tus animales y mantén actualizada tu mochila de vida.
                      <strong> Los animales son familia, nadie se queda atrás.</strong>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>

      {/* PDF Modal - Muestra contenido como HTML */}
      {pdfModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={closePdfModal}>
          <div 
            className="bg-white rounded-2xl shadow-2xl w-[90vw] h-[85vh] max-w-4xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-[#8B9A2D]">Plan de Emergencia: {EMERGENCY_TYPES.find(t => t.id === selectedType)?.label}</h2>
              <div className="flex gap-2">
                <Button
                  onClick={downloadPdfFromModal}
                  className="bg-[#8B9A2D] hover:bg-[#6a6916] text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </Button>
                <Button variant="ghost" onClick={closePdfModal}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-6 bg-slate-50">
              {/* Contenido del plan como HTML */}
              <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 space-y-6">
                {/* Header */}
                <div className="border-b pb-4">
                  <h1 className="text-2xl font-bold text-[#8B9A2D]">
                    Plan: {EMERGENCY_TYPES.find(t => t.id === selectedType)?.label}
                  </h1>
                  <p className="text-slate-500">{EMERGENCY_TYPES.find(t => t.id === selectedType)?.description}</p>
                  <p className="text-sm text-slate-400 mt-2">Emergencia Animal - Nadie se queda atrás</p>
                </div>
                
                {/* ANTES */}
                {guide?.before && (
                  <div>
                    <h2 className="text-lg font-bold text-[#8B9A2D] mb-3 flex items-center gap-2">
                      <Clock className="w-5 h-5" /> ANTES de la emergencia
                    </h2>
                    <ul className="space-y-2">
                      {guide.before.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className={completedItems[`before_${idx}`] ? "text-green-600" : "text-slate-400"}>
                            {completedItems[`before_${idx}`] ? "☑" : "☐"}
                          </span>
                          <span className={completedItems[`before_${idx}`] ? "text-slate-500" : ""}>
                            {item.text}
                            {item.essential && <Badge className="ml-2 bg-amber-100 text-amber-700">Esencial</Badge>}
                            {item.warning && <Badge className="ml-2 bg-red-100 text-red-700">Importante</Badge>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* DURANTE */}
                {guide?.during && (
                  <div>
                    <h2 className="text-lg font-bold text-[#8B9A2D] mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" /> DURANTE la emergencia
                    </h2>
                    <ul className="space-y-2">
                      {guide.during.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className={completedItems[`during_${idx}`] ? "text-green-600" : "text-slate-400"}>
                            {completedItems[`during_${idx}`] ? "☑" : "☐"}
                          </span>
                          <span className={completedItems[`during_${idx}`] ? "text-slate-500" : ""}>
                            {item.text}
                            {item.essential && <Badge className="ml-2 bg-amber-100 text-amber-700">Esencial</Badge>}
                            {item.warning && <Badge className="ml-2 bg-red-100 text-red-700">Importante</Badge>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* DESPUÉS */}
                {guide?.after && (
                  <div>
                    <h2 className="text-lg font-bold text-[#8B9A2D] mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" /> DESPUÉS de la emergencia
                    </h2>
                    <ul className="space-y-2">
                      {guide.after.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className={completedItems[`after_${idx}`] ? "text-green-600" : "text-slate-400"}>
                            {completedItems[`after_${idx}`] ? "☑" : "☐"}
                          </span>
                          <span className={completedItems[`after_${idx}`] ? "text-slate-500" : ""}>
                            {item.text}
                            {item.essential && <Badge className="ml-2 bg-amber-100 text-amber-700">Esencial</Badge>}
                            {item.warning && <Badge className="ml-2 bg-red-100 text-red-700">Importante</Badge>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Teléfonos de emergencia */}
                <div className="bg-slate-100 rounded-lg p-4">
                  <h3 className="font-bold text-slate-700 mb-2">Teléfonos de emergencia</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span>112 - Emergencias generales</span>
                    <span>061 - Emergencias sanitarias</span>
                    <span>080 - Bomberos</span>
                    <span>091 - Policía Nacional</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-amber-50 text-amber-800 text-sm">
              <strong>Nota:</strong> El botón "Descargar PDF" generará el documento. Si no se descarga automáticamente, 
              es debido a restricciones del navegador en el modo preview. En producción funcionará correctamente.
            </div>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
